
import React, { useState, useRef } from 'react';
import {
  User, Mail, Lock, Briefcase, Building, Globe,
  ChevronRight, ChevronLeft, MapPin, AtSign, Loader2, Camera,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import {
  COUNTRIES, COUNTRIES_DATA,
  JOB_CATEGORIES, ADMINISTRATION_TYPES
} from '../constants/registration';
import './RegistrationForm.css';

const DEFAULT_AVATAR = '/img/imagen-por-defecto.png';

const translateAuthError = (msg: string): string => {
  const map: Record<string, string> = {
    'Password is known to be weak and easy to guess, please choose a different one.':
      'La contraseña es demasiado débil o común. Por favor, elige una contraseña más segura.',
    'User already registered':
      'Ya existe una cuenta con ese correo electrónico.',
    'Unable to validate email address: invalid format':
      'El formato del correo electrónico no es válido.',
    'Signup requires a valid password':
      'Debes introducir una contraseña válida.',
  };
  return map[msg] || msg;
};

interface RegistrationFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ onSuccess, onCancel }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signUp } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    jobCategory: '',
    administrationType: '',
    position: '',
    department: '',
    country: 'España',
    region: '',
    interests: [] as string[],
    avatar: '',
    bio: ''
  });

  const [customJobInput, setCustomJobInput] = useState('');
  const [customAdminInput, setCustomAdminInput] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede superar los 5MB');
      return;
    }

    setUploadingAvatar(true);
    setError(null);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('avatars').getPublicUrl(fileName);
      updateField('avatar', data.publicUrl);
    } catch (err: any) {
      console.error('Error uploading avatar:', err);
      setError('Error al subir la imagen: ' + (err.message || 'intentalo de nuevo'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 4) {
      nextStep();
      return;
    }

    setLoading(true);
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const finalJobCategory = formData.jobCategory === 'Otro' ? customJobInput : formData.jobCategory;
      const finalAdminType = formData.administrationType === 'Otro' ? customAdminInput : formData.administrationType;

      await signUp(formData.email, formData.password, {
        name: formData.name,
        lastName: formData.lastName,
        username: formData.username,
        birthDate: formData.birthDate,
        jobCategory: finalJobCategory,
        administrationType: finalAdminType,
        position: formData.position,
        department: formData.department,
        country: formData.country,
        region: formData.region,
        interests: formData.interests,
        avatar: formData.avatar || DEFAULT_AVATAR,
        bio: formData.bio,
        isOrganization: false
      });
      onSuccess();
    } catch (err: any) {
      setError(translateAuthError(err.message) || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.name && formData.lastName && formData.username && 
               formData.email && formData.password && 
               formData.password === formData.confirmPassword &&
               formData.password.length >= 6;
      case 2:
        return formData.jobCategory && (formData.jobCategory !== 'Otro' || customJobInput) &&
               formData.administrationType && (formData.administrationType !== 'Otro' || customAdminInput);
      case 3:
        return formData.position && formData.department && formData.country && formData.region;
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="registration-container animate-fade-in">
      <div className="registration-progress">
        <div 
          className="progress-bar" 
          style={{ width: `${(step / 4) * 100}%` }}
        ></div>
      </div>

      <form onSubmit={handleSubmit} className="registration-form">
        {error && (
          <div className="registration-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {step === 1 && (
          <div className="registration-step animate-slide-in">
            <h2>Crear Cuenta</h2>
            <p>Comienza completando tus datos básicos</p>
            
            <div className="form-row">
              <div className="form-field">
                <label><User size={14} /> Nombre</label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={e => updateField('name', e.target.value)} 
                  required 
                  placeholder="Ej. Ana"
                />
              </div>
              <div className="form-field">
                <label><User size={14} /> Apellidos</label>
                <input 
                  type="text" 
                  value={formData.lastName} 
                  onChange={e => updateField('lastName', e.target.value)} 
                  required 
                  placeholder="Ej. García López"
                />
              </div>
            </div>

            <div className="form-field">
              <label><AtSign size={14} /> Nombre de usuario</label>
              <input
                type="text"
                value={formData.username}
                onChange={e =>
                  updateField(
                    'username',
                    // Filtra en tiempo real: solo letras, números y . _ -
                    // Sin @, sin espacios, sin otros símbolos. Se incluye el
                    // @ explícitamente en los descartes para dejar claro que
                    // no se permite (el sistema ya antepone @ al mostrarlo).
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9._-]/g, '')
                  )
                }
                required
                pattern="^[a-z0-9._-]+$"
                title="Solo letras, números y los signos . _ - (sin @ ni espacios)"
                placeholder="anagarcia"
              />
              <small style={{ display: 'block', marginTop: 6, color: 'var(--color-text-secondary)', fontSize: 12 }}>
                Solo letras, números y los signos <code>.</code> <code>_</code> <code>-</code>. No incluyas <code>@</code>: el sistema lo añade automáticamente al mostrar tu nick.
              </small>
            </div>

            <div className="form-field">
              <label><Mail size={14} /> Email Institucional</label>
              <input 
                type="email" 
                value={formData.email} 
                onChange={e => updateField('email', e.target.value)} 
                required 
                placeholder="ana@novagob.org"
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label><Lock size={14} /> Contraseña</label>
                <input 
                  type="password" 
                  value={formData.password} 
                  onChange={e => updateField('password', e.target.value)} 
                  required 
                  placeholder="••••••••"
                />
              </div>
              <div className="form-field">
                <label><Lock size={14} /> Confirmar</label>
                <input 
                  type="password" 
                  value={formData.confirmPassword} 
                  onChange={e => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))} 
                  required 
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="registration-step animate-slide-in">
            <h2>Perfil Profesional</h2>
            <p>Dinos un poco más sobre tu rol público</p>

            <div className="form-field">
              <label><Briefcase size={14} /> Categoría de puesto</label>
              <select 
                value={formData.jobCategory} 
                onChange={e => updateField('jobCategory', e.target.value)}
                required
              >
                <option value="">Selecciona una categoría</option>
                {JOB_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {formData.jobCategory === 'Otro' && (
                <input 
                  type="text" 
                  className="mt-2"
                  placeholder="Especifica tu categoría" 
                  value={customJobInput}
                  onChange={e => setCustomJobInput(e.target.value)}
                />
              )}
            </div>

            <div className="form-field">
              <label><Building size={14} /> Tipo de Administración</label>
              <select 
                value={formData.administrationType} 
                onChange={e => updateField('administrationType', e.target.value)}
                required
              >
                <option value="">Selecciona tipo de administración</option>
                {ADMINISTRATION_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {formData.administrationType === 'Otro' && (
                <input 
                  type="text" 
                  className="mt-2"
                  placeholder="Especifica tipo de administración" 
                  value={customAdminInput}
                  onChange={e => setCustomAdminInput(e.target.value)}
                />
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="registration-step animate-slide-in">
            <h2>Detalles del Cargo</h2>
            <p>¿Dónde trabajas actualmente?</p>

            <div className="form-field">
              <label>Cargo / Puesto</label>
              <input 
                type="text" 
                value={formData.position} 
                onChange={e => updateField('position', e.target.value)} 
                required 
                placeholder="Ej. Directora General de Innovación"
              />
            </div>

            <div className="form-field">
              <label>Institución / Organización / Empresa</label>
              <input
                type="text"
                value={formData.department}
                onChange={e => updateField('department', e.target.value)}
                required
                placeholder="Ej. Ayuntamiento de Madrid"
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label><Globe size={14} /> País</label>
                <select 
                  value={formData.country} 
                  onChange={e => updateField('country', e.target.value)}
                  required
                >
                  {COUNTRIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label><MapPin size={14} /> Región / CCAA</label>
                <select 
                  value={formData.region} 
                  onChange={e => updateField('region', e.target.value)}
                  required
                >
                  <option value="">Selecciona región</option>
                  {COUNTRIES_DATA[formData.country]?.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="registration-step animate-slide-in">
            <h2>Foto de Perfil</h2>
            <p>Personaliza tu perfil de NovaGob</p>

            <div className="form-field">
              <label><Camera size={14} /> Foto de Perfil (Opcional)</label>
              <div className="avatar-upload">
                {formData.avatar ? (
                  <img src={formData.avatar} alt="Profile" className="avatar-preview" />
                ) : (
                  <div
                    className="avatar-preview"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.05)',
                      border: '2px dashed rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.4)',
                    }}
                  >
                    <Camera size={24} />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarUpload}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? 'Subiendo...' : formData.avatar ? 'Cambiar foto' : 'Subir Foto'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="registration-actions">
          {step > 1 ? (
            <button 
              type="button" 
              onClick={prevStep} 
              className="btn btn-outline"
              disabled={loading}
            >
              <ChevronLeft size={18} /> Anterior
            </button>
          ) : (
            <button 
              type="button" 
              onClick={onCancel} 
              className="btn btn-outline"
              disabled={loading}
            >
              Cancelar
            </button>
          )}

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={!isStepValid() || loading}
          >
            {loading ? (
              <><Loader2 className="animate-spin mr-2" size={18} /> Procesando...</>
            ) : step === 4 ? (
              'Finalizar Registro'
            ) : (
              <>Siguiente <ChevronRight size={18} /></>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
